import {
  ADD_NEW_PAYTO_ACCOUNT_MAX_AMOUNT_DEFAULT,
  FORM_ADD_PAYTO_MAX_AMOUNT_NAME,
  FORM_ADD_PAYTO_PAYID_TYPE_NAME,
  PAYID_TYPE_MOBILE,
  RESET_FORM_ADD_PAYTO_ACCOUNT_STORE,
} from 'features/to/const';
import useStore from 'hooks/useStore';
import {
  SET_ACCOUNT_AGREEMENT_EDITING,
  SET_ADD_PAYTO_ACCOUNT_INFORMATION,
  SET_PAYTO_ACCOUNTS_AGREEMENT,
  SET_PAYTO_ACCOUNT_AUTHORISING,
  SET_PAYTO_AMEND_LIMIT,
  SET_PAYTO_BANK_ACCOUNTS_VERIFIED,
  SET_PAYTO_BSB_ACCOUNT_CHECK_PENDING,
  SET_PAYTO_BSB_NUMBER_CHECK_PENDING,
  SET_PAYTO_CREATE_LIMIT,
  SET_PAYTO_MAX_AMOUNT_EDITED,
  SET_PAYTO_PAYID_BANK_VALIDATION_FAILURE,
  SET_PAYTO_PAYID_CHECK_PENDING,
  SET_PAYTO_PAYID_GENERATION,
} from 'store/action';

const usePaytoStore = () => {
  const { state, setState, dispatch } = useStore();
  const {
    paytoBankAccountsVerified,
    paytoAccountsAgreement,
    paytoAccountInformation,
    isPaytoMaxAmountEdited,
    paytoAccountAuthorising,
    isAccountAgreementEditing,
    paytoPayIdGeneration,
    paytoCreateLimit,
    paytoAmendLimit,
    paytoBsbNumberCheckPending,
    paytoBsbAccountCheckPending,
    paytoPayIdCheckPending,
    paytoPayIdBankValidationFailure,
  } = state || {};

  const maxAmount = Number(
    paytoAccountInformation?.[FORM_ADD_PAYTO_MAX_AMOUNT_NAME] ||
      ADD_NEW_PAYTO_ACCOUNT_MAX_AMOUNT_DEFAULT
  );

  const payIdType =
    paytoAccountInformation?.[FORM_ADD_PAYTO_PAYID_TYPE_NAME] ||
    PAYID_TYPE_MOBILE;

  const setPaytoBankAccountsVerified = (payload) =>
    setState({
      action: SET_PAYTO_BANK_ACCOUNTS_VERIFIED,
      payload: payload || null,
    });

  const setPaytoAccountsAgreement = (payload) =>
    setState({
      action: SET_PAYTO_ACCOUNTS_AGREEMENT,
      payload: payload || null,
    });

  const setPaytoMaxAmountEdited = (payload) =>
    setState({
      action: SET_PAYTO_MAX_AMOUNT_EDITED,
      payload: payload || null,
    });

  const setPaytoAccountInformation = (payload) =>
    setState({
      action: SET_ADD_PAYTO_ACCOUNT_INFORMATION,
      payload: payload,
    });

  const setPaytoAccountAuthorising = (payload = null) =>
    setState({
      action: SET_PAYTO_ACCOUNT_AUTHORISING,
      payload,
    });

  const setPaytoAccountAgreementEditing = (payload) =>
    setState({
      action: SET_ACCOUNT_AGREEMENT_EDITING,
      payload,
    });

  const setPaytoPayIdGeneration = (payload) =>
    setState({
      action: SET_PAYTO_PAYID_GENERATION,
      payload,
    });

  // DEV-1401: authoritative createPayToLimit for the Add PayTo (Create) flow.
  // Shared store slot rather than component state so it survives the BSB
  // and PayID HOCs writing to it independently, and so it can be reset
  // alongside paytoAccountInformation below.
  const setPaytoCreateLimit = (payload = null) =>
    setState({
      action: SET_PAYTO_CREATE_LIMIT,
      payload,
    });

  // DEV-1401: authoritative amendPayToLimit for the Add PayTo (Create) flow,
  // mirroring setPaytoCreateLimit above -- see store/index.js's initial-state
  // comment for why this is a separate slot from paytoAccountInformation.
  const setPaytoAmendLimit = (payload = null) =>
    setState({
      action: SET_PAYTO_AMEND_LIMIT,
      payload,
    });

  const setPaytoPayIdType = (payIdType = PAYID_TYPE_MOBILE) =>
    setState({
      action: SET_ADD_PAYTO_ACCOUNT_INFORMATION,
      payload: {
        ...(paytoAccountInformation || {}),
        [FORM_ADD_PAYTO_PAYID_TYPE_NAME]: payIdType,
      },
    });

  // DEV-1401: per-call pending signals for the Create-flow limit lookups.
  // Deliberately separate from SET_SHOW_LOADING -- see action.js comment.
  const setPaytoBsbNumberCheckPending = (payload = false) =>
    setState({
      action: SET_PAYTO_BSB_NUMBER_CHECK_PENDING,
      payload,
    });

  const setPaytoBsbAccountCheckPending = (payload = false) =>
    setState({
      action: SET_PAYTO_BSB_ACCOUNT_CHECK_PENDING,
      payload,
    });

  const setPaytoPayIdCheckPending = (payload = false) =>
    setState({
      action: SET_PAYTO_PAYID_CHECK_PENDING,
      payload,
    });

  const setPaytoPayIdBankValidationFailure = (payload = false) =>
    setState({
      action: SET_PAYTO_PAYID_BANK_VALIDATION_FAILURE,
      payload,
    });

  const resetPaytoAccountInformation = (payload = {}) => {
    setState({
      action: SET_ADD_PAYTO_ACCOUNT_INFORMATION,
      payload: { ...RESET_FORM_ADD_PAYTO_ACCOUNT_STORE, ...payload },
    });
    // DEV-1401: reset createPayToLimit/amendPayToLimit alongside Add PayTo
    // form state so a limit resolved for one recipient/method cannot leak
    // into the next (e.g. closing the modal, switching BSB<->PayID, or
    // moving to a new recipient all go through this same reset path
    // already).
    setPaytoCreateLimit(null);
    setPaytoAmendLimit(null);
    // Also reset the pending signals -- if the modal is closed/reset while
    // a validation is still in flight, its eventual resolution must not
    // apply against a leftover pending flag from a prior recipient/method.
    setPaytoBsbNumberCheckPending(false);
    setPaytoBsbAccountCheckPending(false);
    setPaytoPayIdCheckPending(false);
    setPaytoPayIdBankValidationFailure(false);
  };

  const resetPaytoMaxAmountEdited = () => {
    setPaytoMaxAmountEdited(false);
  };

  const resetPaytoAccountAuthorising = () => {
    setPaytoAccountAuthorising(null);
  };

  const resetPaytoAgreementEditing = () => {
    setPaytoAccountAgreementEditing(false);
  };

  const resetPaytoAccountsAgreement = (
    isResetPaytoAccountInformation = true,
    paytoAccountsAgreement = null
  ) => {
    setPaytoAccountsAgreement(paytoAccountsAgreement);
    if (isResetPaytoAccountInformation) {
      resetPaytoAccountInformation();
    }
  };

  return {
    ...state,
    dispatch,
    paytoAccountsAgreement,
    paytoBankAccountsVerified,
    maxAmount,
    setPaytoAccountInformation,
    resetPaytoAccountInformation,
    setPaytoBankAccountsVerified,
    setPaytoAccountsAgreement,
    isPaytoMaxAmountEdited,
    setPaytoMaxAmountEdited,
    paytoAccountAuthorising,
    setPaytoAccountAuthorising,
    isAccountAgreementEditing,
    setPaytoAccountAgreementEditing,
    payIdType,
    setPaytoPayIdType,
    paytoPayIdGeneration,
    setPaytoPayIdGeneration,
    paytoCreateLimit,
    setPaytoCreateLimit,
    paytoAmendLimit,
    setPaytoAmendLimit,
    paytoBsbNumberCheckPending,
    setPaytoBsbNumberCheckPending,
    paytoBsbAccountCheckPending,
    setPaytoBsbAccountCheckPending,
    paytoPayIdCheckPending,
    setPaytoPayIdCheckPending,
    paytoPayIdBankValidationFailure,
    setPaytoPayIdBankValidationFailure,
    resetPaytoMaxAmountEdited,
    resetPaytoAccountAuthorising,
    resetPaytoAgreementEditing,
    resetPaytoAccountsAgreement,
  };
};

export default usePaytoStore;
